import { useEffect, useRef } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { IconName } from '@fortawesome/fontawesome-common-types'
import {
    Box,
    Button,
    CardActions,
    CardMedia,
    Skeleton,
    Stack,
    useMediaQuery
} from '@mui/material'
import {
    CoreTypography,
    LeafCopyButton,
    useTheme,
    LeafFixedMediaCard
} from '@thriveglobal/thrive-web-leafkit'
import { CardDetails, CardStatus } from '../Card'

const intlMessages = defineMessages({
    completed: {
        defaultMessage: 'Completed',
        description: `Something has been completed`
    },
    copyTitleToClipboard: {
        defaultMessage: 'Copy to clipboard {title}',
        description: 'Copy to clipboard - followed by the content title'
    },
    copyToClipboard: {
        defaultMessage: 'Copy to clipboard',
        description: `Aria label for compying a link to the clipboard`
    },
    viewArticle: {
        defaultMessage: 'View article',
        description: 'Click to view article'
    },
    navigate: {
        defaultMessage: 'Navigate to {title}',
        description: 'Navigate to - followed by the content title'
    }
})

const messages = defineMessages({
    featureCardAlt: {
        defaultMessage: 'Feature card image',
        description: 'Featured card image alt'
    }
})

export default function FeaturedCard({
    contentType,
    type,
    title,
    subTitle,
    typeIcon,
    imageSrc,
    durationText,
    rootMargin = '0px 0px 0px 0px',
    root = null,
    threshold = 0.5,
    showLoading = false,
    shareUrl,
    isComplete,
    action,
    onEnter
}: {
    contentType?: string
    type?: string
    title?: string
    subTitle?: string
    typeIcon?: IconName
    imageSrc?: string
    durationText?: string
    rootMargin?: string
    root?: null | Element
    threshold?: number
    showLoading?: boolean
    shareUrl?: string
    isComplete?: boolean
    actionText?: string
    action?: { title: string; onClick?: () => void }
    onEnter?: (entry: IntersectionObserverEntry) => void
}) {
    const intl = useIntl()
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!onEnter || !ref.current) return

        const el = ref.current

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) onEnter(entry)
            },
            { rootMargin, threshold, root }
        )

        observer.observe(el)

        return () => observer.unobserve(el)
    }, [onEnter, root, rootMargin, threshold])

    return (
        <LeafFixedMediaCard
            media={
                <CardMedia
                    component="img"
                    image={imageSrc}
                    alt={intl.formatMessage(messages.featureCardAlt)}
                />
            }
            ref={ref}
            data-testid={`learn-featured-${type}`}
            alignMedia={isSmallScreen ? 'top' : 'left'}
        >
            <Stack
                width={'100%'}
                height={'100%'}
                gap={2}
                justifyContent={'space-between'}
            >
                <CardDetails
                    title={title}
                    titleVariant="h3"
                    subTitle={subTitle}
                    topLeftContent={
                        showLoading ? (
                            <Skeleton variant="text" width={120} height={50} />
                        ) : (
                            <CardStatus
                                label={contentType}
                                iconName={typeIcon}
                            />
                        )
                    }
                    topRightContent={
                        showLoading ? (
                            <Skeleton variant="text" width={120} height={50} />
                        ) : (
                            <CardStatus
                                label={
                                    isComplete
                                        ? intl.formatMessage(
                                              intlMessages.completed
                                          )
                                        : durationText
                                }
                                iconName={isComplete ? 'check' : undefined}
                                variant={isComplete ? 'success' : undefined}
                            />
                        )
                    }
                    bottomLeftContent={
                        showLoading ? (
                            <Skeleton variant="text" width={120} height={50} />
                        ) : (
                            action && (
                                <CardActions sx={{ p: 0, m: 0 }}>
                                    <Button
                                        variant="contained"
                                        role="button"
                                        size="large"
                                        onClick={action.onClick}
                                        data-testid={`learn-featured-${type}-button`}
                                        aria-label={intl.formatMessage(
                                            intlMessages.viewArticle
                                        )}
                                        name={intl.formatMessage(
                                            intlMessages.navigate,
                                            {
                                                title
                                            }
                                        )}
                                    >
                                        <CoreTypography
                                            customVariant="buttonLarge"
                                            sx={{
                                                textAlign: 'center',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {action.title}
                                        </CoreTypography>
                                    </Button>
                                </CardActions>
                            )
                        )
                    }
                    bottomRightContent={
                        shareUrl && (
                            <CardActions sx={{ p: 0, m: 0 }}>
                                <Box
                                    sx={{
                                        mt: -2,
                                        marginLeft: 'auto'
                                    }}
                                >
                                    <LeafCopyButton
                                        ariaLabel={
                                            title
                                                ? intl.formatMessage(
                                                      intlMessages.copyTitleToClipboard,
                                                      {
                                                          title
                                                      }
                                                  )
                                                : intl.formatMessage(
                                                      intlMessages.copyToClipboard
                                                  )
                                        }
                                        icon="link"
                                        linkBody={`${window.location.origin}${shareUrl}`}
                                        noLabel
                                        toolTipPosition="top"
                                    />
                                </Box>
                            </CardActions>
                        )
                    }
                />
            </Stack>
        </LeafFixedMediaCard>
    )
}
